/**
 * Cola ANMAT — vista de SOLO LECTURA de las trazas al SNT (tabla `traza_job`).
 *
 * Una fila por (referencia, tipo). Si una referencia fue re-trazada, solo se
 * ve la última traza — la histórica queda en la tabla pero no se muestra acá.
 *
 * Sin acciones de mutación (reintentar / cancelar / editar). El detalle abre
 * un modal con el request + result payloads + códigos de transacción ANMAT.
 *
 * Auto-refresh 3s para reflejar transiciones del poller (pending → running →
 * success/error/failed).
 */

import { useMemo, useState } from 'react';
import { Search, ListTree, Eye, Loader2, X } from 'lucide-react';
import { useAnmatCola, useTrazaJobByReferencia } from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { fmtDateTime, fmtDateTimeLong, fmtRemitoNumero } from '@/lib/format';
import type {
  AnmatColaItem,
  AnmatColaStatus,
  AnmatColaTipo,
} from '@/api/contracts/anmat-cola';

const STATUS_LABEL: Record<AnmatColaStatus, string> = {
  pending: 'En cola',
  running: 'En curso',
  success: 'OK',
  error: 'Error',
  failed: 'Falló',
  anulada: 'Anulada',
};

const STATUS_BADGE: Record<AnmatColaStatus, string> = {
  pending: 'badge-blue',
  running: 'badge-blue',
  success: 'badge-green',
  error: 'badge-red',
  failed: 'badge-red',
  anulada: 'badge-gray',
};

const TIPO_LABEL: Record<AnmatColaTipo, string> = {
  egreso: 'Egreso',
  ingreso: 'Ingreso',
  cancelacion: 'Cancelación',
};

const TIPO_BADGE: Record<AnmatColaTipo, string> = {
  egreso: 'badge-orange',
  ingreso: 'badge-blue',
  cancelacion: 'badge-red',
};

export function AnmatPage() {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const glnId = ubicacionActivaId ? Number(ubicacionActivaId) : null;

  const [filtroEstado, setFiltroEstado] = useState<'all' | AnmatColaStatus>('all');
  const [filtroTipo, setFiltroTipo] = useState<'all' | AnmatColaTipo>('all');
  const [busqueda, setBusqueda] = useState('');
  const [detalleAbierto, setDetalleAbierto] = useState<AnmatColaItem | null>(null);

  const { data, isLoading, error } = useAnmatCola(
    glnId
      ? {
          glnId,
          status: filtroEstado === 'all' ? null : filtroEstado,
          tipo: filtroTipo === 'all' ? null : filtroTipo,
          q: busqueda.trim() || null,
        }
      : null,
  );

  const items = useMemo(() => data?.items ?? [], [data]);

  // KPIs sobre todas las filas devueltas (server ya filtró por GLN y los
  // demás filtros aplicados — esto es lo que el usuario está viendo).
  const kpis = useMemo(() => {
    return {
      total: items.length,
      enCola: items.filter((o) => o.status === 'pending' || o.status === 'running').length,
      ok: items.filter((o) => o.status === 'success').length,
      errores: items.filter((o) => o.status === 'error' || o.status === 'failed').length,
    };
  }, [items]);

  if (!glnId) {
    return (
      <Alert>
        <AlertDescription>
          Seleccioná un GLN activo para ver la cola ANMAT.
        </AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Total" value={kpis.total} />
        <Kpi label="En cola" value={kpis.enCola} tone="blue" />
        <Kpi label="OK" value={kpis.ok} tone="green" />
        <Kpi label="Errores" value={kpis.errores} tone="red" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-everex-azul">
            <ListTree className="h-5 w-5" />
            Log ANMAT
          </CardTitle>
          <p className="text-xs text-gray-600">
            Todas las trazas al SNT del GLN activo — última por referencia. Solo lectura.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <Label className="mb-1 block text-xs">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600" />
                <Input
                  className="pl-9 font-mono"
                  placeholder="Referencia o nº remito"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Estado</Label>
              <Select
                value={filtroEstado}
                onValueChange={(v) => setFiltroEstado(v as 'all' | AnmatColaStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {(Object.keys(STATUS_LABEL) as AnmatColaStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Tipo</Label>
              <Select
                value={filtroTipo}
                onValueChange={(v) => setFiltroTipo(v as 'all' | AnmatColaTipo)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="egreso">Egreso</SelectItem>
                  <SelectItem value="ingreso">Ingreso</SelectItem>
                  <SelectItem value="cancelacion">Cancelación</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px]">Fecha</TableHead>
                  <TableHead className="text-[11px]">Tipo</TableHead>
                  <TableHead className="text-[11px]">Usuario</TableHead>
                  <TableHead className="text-[11px]">Referencia</TableHead>
                  <TableHead className="text-[11px]">Nº Remito</TableHead>
                  <TableHead className="text-[11px]">Items</TableHead>
                  <TableHead className="text-[11px]">Estado</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-gray-600">
                      <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                      Cargando cola...
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-xs text-gray-500">
                      Sin operaciones para estos filtros.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((op) => (
                    <ColaRow
                      key={op.id}
                      op={op}
                      onVer={() => setDetalleAbierto(op)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="text-[11px] text-gray-600">
            Auto-refresh cada 3s. {items.length} fila{items.length === 1 ? '' : 's'}.
          </div>
        </CardContent>
      </Card>

      <DetalleDialog
        op={detalleAbierto}
        onClose={() => setDetalleAbierto(null)}
      />
    </div>
  );
}

function ColaRow({ op, onVer }: { op: AnmatColaItem; onVer: () => void }) {
  const items =
    op.totalItems != null
      ? `${op.doneItems ?? 0}/${op.totalItems}${
          op.errorItems && op.errorItems > 0 ? ` (${op.errorItems} err)` : ''
        }`
      : '—';
  return (
    <TableRow className="text-xs">
      <TableCell className="whitespace-nowrap text-[11px] text-gray-600">
        {op.createdAt ? fmtDateTimeLong(op.createdAt) : '—'}
      </TableCell>
      <TableCell>
        <span className={cn('badge', TIPO_BADGE[op.tipo])}>{TIPO_LABEL[op.tipo]}</span>
      </TableCell>
      <TableCell>
        <div>
          <div className="font-medium">{op.usuario.nombre ?? '—'}</div>
          <div className="text-[10px] text-gray-500">@{op.usuario.username}</div>
        </div>
      </TableCell>
      <TableCell className="font-mono text-[11px]">{op.referencia}</TableCell>
      <TableCell className="font-mono text-[11px]">
        {op.numeroRemito ? fmtRemitoNumero(op.numeroRemito) : '—'}
      </TableCell>
      <TableCell className="text-[11px]">{items}</TableCell>
      <TableCell>
        <span className={cn('badge', STATUS_BADGE[op.status])}>
          {STATUS_LABEL[op.status]}
        </span>
        {op.errorMessage && (
          <div
            className="mt-0.5 max-w-[200px] truncate text-[11px] text-red-600"
            title={op.errorMessage}
          >
            {op.errorMessage}
          </div>
        )}
      </TableCell>
      <TableCell>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2"
          onClick={onVer}
          title="Ver detalle"
        >
          <Eye className="h-3.5 w-3.5" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function DetalleDialog({
  op,
  onClose,
}: {
  op: AnmatColaItem | null;
  onClose: () => void;
}) {
  // Mientras el modal esté cerrado pasamos null → hook deshabilitado.
  const detalleQ = useTrazaJobByReferencia(
    op ? op.referencia : null,
    op ? op.tipo : 'egreso',
  );
  const detalle = detalleQ.data;

  return (
    <Dialog open={op !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>Detalle traza</span>
            {op && (
              <span className="font-mono text-xs text-gray-600">
                {op.referencia} · {TIPO_LABEL[op.tipo]}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {!op ? null : detalleQ.isLoading ? (
          <div className="flex items-center justify-center py-8 text-gray-600">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Cargando detalle...
          </div>
        ) : !detalle ? (
          <Alert>
            <AlertDescription>No hay detalle disponible.</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-2 gap-3 rounded border border-gray-100 bg-gray-50 p-3">
              <Field label="Estado">
                <span className={cn('badge', STATUS_BADGE[detalle.status])}>
                  {STATUS_LABEL[detalle.status]}
                </span>
              </Field>
              <Field label="Job ID (riga)">
                <span className="font-mono">{detalle.jobId}</span>
              </Field>
              <Field label="Creado">{fmtDateTime(detalle.createdAt)}</Field>
              <Field label="Finalizado">{fmtDateTime(detalle.finishedAt) ?? '—'}</Field>
              <Field label="Items">
                <span className="font-mono">
                  {detalle.doneItems ?? 0}/{detalle.totalItems ?? 0}
                  {detalle.errorItems && detalle.errorItems > 0
                    ? ` · ${detalle.errorItems} err`
                    : ''}
                </span>
              </Field>
              <Field label="Chunks">
                <span className="font-mono">
                  {detalle.doneChunks ?? 0}/{detalle.totalChunks ?? 0}
                </span>
              </Field>
              <Field label="Polling checks">
                <span className="font-mono">{detalle.statusCheckCount}</span>
              </Field>
              <Field label="Último check">
                {fmtDateTime(detalle.lastStatusCheckAt) ?? '—'}
              </Field>
              <Field label="Usuario">
                {detalle.usuario.nombre} ({detalle.usuario.username})
              </Field>
              <Field label="GLN">
                {detalle.gln.nombre}{' '}
                <span className="font-mono text-[10px] text-gray-500">
                  {detalle.gln.gln}
                </span>
              </Field>
            </div>

            {detalle.errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>
                  <div className="font-semibold">Error:</div>
                  <div className="mt-0.5 font-mono text-[11px]">{detalle.errorMessage}</div>
                </AlertDescription>
              </Alert>
            )}

            {detalle.codigosTransaccion.length > 0 && (
              <section>
                <h3 className="mb-1 text-[11px] font-semibold uppercase text-gray-600">
                  Códigos de transacción ANMAT
                </h3>
                <div className="flex flex-wrap gap-1">
                  {detalle.codigosTransaccion.map((c) => (
                    <span
                      key={c}
                      className="rounded bg-green-100 px-2 py-0.5 font-mono text-[10px] text-green-800"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {detalle.resultPayload?.transacciones &&
              detalle.resultPayload.transacciones.length > 0 && (
                <section>
                  <h3 className="mb-1 text-[11px] font-semibold uppercase text-gray-600">
                    Transacciones ({detalle.resultPayload.transacciones.length})
                  </h3>
                  <div className="space-y-2">
                    {detalle.resultPayload.transacciones.map((tx, i) => (
                      <div
                        key={i}
                        className={cn(
                          'rounded border p-2 text-[11px]',
                          tx.error
                            ? 'border-red-200 bg-red-50'
                            : 'border-green-200 bg-green-50',
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono">
                            {tx.codigoTransaccion ?? '(sin código)'}
                          </span>
                          {tx.error ? (
                            <span className="text-red-700">{tx.error}</span>
                          ) : (
                            <span className="text-green-700">OK</span>
                          )}
                        </div>
                        {tx.items && tx.items.length > 0 && (
                          <div className="mt-1 text-[10px] text-gray-600">
                            {tx.items.length} item{tx.items.length === 1 ? '' : 's'}
                            {' · '}
                            {tx.items
                              .slice(0, 3)
                              .map((it) => it.numero_serial ?? '?')
                              .join(', ')}
                            {tx.items.length > 3 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

            <details className="rounded border border-gray-200">
              <summary className="cursor-pointer bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-700">
                Request payload
              </summary>
              <pre className="overflow-x-auto bg-white p-3 font-mono text-[10px] text-gray-700">
                {JSON.stringify(detalle.requestPayload, null, 2)}
              </pre>
            </details>

            <details className="rounded border border-gray-200">
              <summary className="cursor-pointer bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-700">
                Result payload
              </summary>
              <pre className="overflow-x-auto bg-white p-3 font-mono text-[10px] text-gray-700">
                {JSON.stringify(detalle.resultPayload, null, 2)}
              </pre>
            </details>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="mr-1 h-3 w-3" />
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-gray-500">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Kpi({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'blue' | 'green' | 'red';
}) {
  const toneClass = {
    default: 'text-everex-azul',
    blue: 'text-blue-700',
    green: 'text-green-700',
    red: 'text-red-700',
  }[tone];
  return (
    <Card>
      <CardContent className="pt-4">
        <div className={cn('text-3xl font-bold', toneClass)}>{value}</div>
        <div className="text-[11px] uppercase text-gray-600">{label}</div>
      </CardContent>
    </Card>
  );
}
